import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Lock, Sparkles, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SymbolComboBox } from '../../components/ui/symbol-combobox';
import { getTradeableAssets, getRecentAssets } from '../../services/tradeableAssetsService';
import { getAssetParams, getQuantityLabel, calculatePips, calculateProfit } from '../../utils/tradeCalculations';
import { uploadTradeScreenshots } from '../../services/screenshotService';
import { useSubscription } from '../../hooks/useSubscription';
import { trackTradeAdded } from '../../services/analytics';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const SCREENSHOTS_PER_TRADE = { free: 0, basic: 1, pro: 3, elite: 5 };

const AddTradeModal = ({ onClose, onSave }) => {
  const [assets, setAssets] = useState([]);
  const [recentAssets, setRecentAssets] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitLabel, setSubmitLabel] = useState('Save Trade');

  // Pending screenshot files (staged locally, uploaded after trade is saved)
  const [pendingFiles, setPendingFiles] = useState([]); // [{ file: File, previewUrl: string }]
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);

  // Lightbox for staged file preview
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const lightboxPrev = useCallback(() => setLightboxIndex(i => (i - 1 + pendingFiles.length) % pendingFiles.length), [pendingFiles.length]);
  const lightboxNext = useCallback(() => setLightboxIndex(i => (i + 1) % pendingFiles.length), [pendingFiles.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, lightboxPrev, lightboxNext, closeLightbox]);

  const { planId } = useSubscription();
  const screenshotLimit = SCREENSHOTS_PER_TRADE[planId] ?? 0;
  const canUpload = screenshotLimit > 0;
  const canAnalyze = planId === 'pro' || planId === 'elite';

  useEffect(() => {
    getTradeableAssets().then(setAssets).catch(console.error);
    getRecentAssets().then(setRecentAssets).catch(console.error);
  }, []);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => pendingFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
  }, []); // intentional: run only on mount/unmount

  const [trade, setTrade] = useState({
    symbol: '',
    direction: 'BUY',
    entry: '',
    exit: '',
    lotSize: '',
    profit: '',
    pips: '',
    openTime: new Date().toISOString().slice(0, 16),
    closeTime: new Date().toISOString().slice(0, 16),
    notes: '',
    commission: '',
    swap: '',
    stopLoss: '',
    takeProfit: '',
  });

  const assetParams = getAssetParams(trade.symbol, assets);
  const quantityLabel = assetParams ? getQuantityLabel(assetParams.category) : 'Quantity';
  const isAutoCalc = !!assetParams;

  let autoPips = null;
  let autoProfit = null;
  if (isAutoCalc) {
    autoPips = calculatePips({
      entryPrice: trade.entry,
      exitPrice: trade.exit,
      direction: trade.direction,
      pipSize: assetParams.pipSize,
    });
    autoProfit = calculateProfit({
      entryPrice: trade.entry,
      exitPrice: trade.exit,
      direction: trade.direction,
      quantity: trade.lotSize,
      contractSize: assetParams.contractSize,
    });
  }

  const displayPips = isAutoCalc ? (autoPips ?? '') : trade.pips;
  const displayProfit = isAutoCalc ? (autoProfit ?? '') : trade.profit;

  // Debug: remove this console.log once auto-calc is confirmed working
  console.log('[AddTrade AutoCalc]', { isAutoCalc, symbol: trade.symbol, entry: trade.entry, exit: trade.exit, direction: trade.direction, lotSize: trade.lotSize, pipSize: assetParams?.pipSize, contractSize: assetParams?.contractSize, autoPips, autoProfit, displayPips, displayProfit, assetsLoaded: assets.length });

  // ─── File staging ──────────────────────────────────────────────────────────

  const addFiles = (fileList) => {
    setFileError(null);
    const incoming = Array.from(fileList);
    const remaining = screenshotLimit - pendingFiles.length;

    if (incoming.length > remaining) {
      setFileError(`Your plan allows ${screenshotLimit} screenshot${screenshotLimit === 1 ? '' : 's'} per trade.`);
      return;
    }

    const invalid = incoming.filter(f => !ALLOWED_TYPES.includes(f.type) || f.size > MAX_FILE_SIZE);
    if (invalid.length > 0) {
      setFileError(invalid.map(f =>
        !ALLOWED_TYPES.includes(f.type) ? `${f.name}: not an image` : `${f.name}: exceeds 5MB`
      ).join(', '));
      return;
    }

    const staged = incoming.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingFiles(prev => [...prev, ...staged]);
  };

  const removeFile = (index) => {
    setPendingFiles(prev => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!trade.openTime || !trade.closeTime) {
      alert('Please fill in both Entry and Exit Date/Time fields');
      return;
    }

    const tradeToSave = {
      ...trade,
      pips: isAutoCalc ? autoPips : trade.pips,
      profit: isAutoCalc ? autoProfit : trade.profit,
      openTime: trade.openTime ? new Date(trade.openTime).toISOString() : null,
      closeTime: trade.closeTime ? new Date(trade.closeTime).toISOString() : null,
    };

    setIsSubmitting(true);
    try {
      // Step 1: save trade
      setSubmitLabel('Saving...');
      const savedTrade = await onSave(tradeToSave);

      // Step 2: upload staged screenshots if any
      if (savedTrade?.id && pendingFiles.length > 0) {
        setSubmitLabel(canAnalyze ? 'Analyzing charts...' : 'Uploading...');
        await uploadTradeScreenshots(savedTrade.id, pendingFiles.map(f => f.file));
      }

      trackTradeAdded({
        symbol: tradeToSave.symbol,
        direction: tradeToSave.direction,
        has_screenshots: pendingFiles.length > 0,
        screenshot_count: pendingFiles.length,
      });
      onClose();
    } catch (error) {
      console.error('Error saving trade:', error);
      setSubmitLabel('Save Trade');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={onClose}>

      {/* Modal */}
      <div className="relative w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-card border border-white/[0.08] rounded-xl shadow-modal flex flex-col min-h-0 flex-1 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-border flex-shrink-0">
            <h2 className="text-lg md:text-xl font-bold text-foreground">Add New Trade</h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-accent">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Symbol */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="symbol">Symbol</Label>
                  <SymbolComboBox
                    id="symbol"
                    value={trade.symbol}
                    onChange={(symbol) => setTrade({ ...trade, symbol })}
                    assets={assets}
                    recentAssets={recentAssets}
                    placeholder="Search symbol (e.g. EURUSD)"
                    className="bg-muted border-border"
                    required
                  />
                </div>

                {/* Direction */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="direction">Direction</Label>
                  <Select
                    value={trade.direction}
                    onValueChange={(value) => setTrade({ ...trade, direction: value })}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select direction" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUY" className="text-green-500">BUY</SelectItem>
                      <SelectItem value="SELL" className="text-red-500">SELL</SelectItem>
                      <SelectItem value="LONG" className="text-green-500">LONG</SelectItem>
                      <SelectItem value="SHORT" className="text-red-500">SHORT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Entry Price */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="entry">Entry Price</Label>
                  <Input
                    id="entry"
                    type="number"
                    step="0.00001"
                    value={trade.entry}
                    onChange={(e) => setTrade({ ...trade, entry: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>

                {/* Exit Price */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="exit">Exit Price</Label>
                  <Input
                    id="exit"
                    type="number"
                    step="0.00001"
                    value={trade.exit}
                    onChange={(e) => setTrade({ ...trade, exit: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>

                {/* Stop Loss */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="stopLoss">Stop Loss <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="stopLoss"
                    type="number"
                    step="0.00001"
                    placeholder="Price"
                    value={trade.stopLoss}
                    onChange={(e) => setTrade({ ...trade, stopLoss: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>

                {/* Take Profit */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="takeProfit">Take Profit <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="takeProfit"
                    type="number"
                    step="0.00001"
                    placeholder="Price"
                    value={trade.takeProfit}
                    onChange={(e) => setTrade({ ...trade, takeProfit: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>

                {/* Quantity (dynamic label) */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="lotSize">{quantityLabel}</Label>
                  <Input
                    id="lotSize"
                    type="number"
                    step="0.01"
                    value={trade.lotSize}
                    onChange={(e) => setTrade({ ...trade, lotSize: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>

                {/* Profit (auto-calculated) */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="profit">
                    Profit/Loss {isAutoCalc && <span className="text-xs text-muted-foreground">(auto)</span>}
                  </Label>
                  <Input
                    id="profit"
                    type="number"
                    step="0.01"
                    value={displayProfit}
                    onChange={isAutoCalc ? undefined : (e) => setTrade({ ...trade, profit: e.target.value })}
                    readOnly={isAutoCalc}
                    className={isAutoCalc ? "bg-muted/50 border-border cursor-not-allowed" : "bg-muted border-border"}
                    tabIndex={isAutoCalc ? -1 : undefined}
                  />
                </div>

                {/* Pips (auto-calculated) */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="pips">
                    Pips {isAutoCalc && <span className="text-xs text-muted-foreground">(auto)</span>}
                  </Label>
                  <Input
                    id="pips"
                    type="number"
                    step="0.1"
                    value={displayPips}
                    onChange={isAutoCalc ? undefined : (e) => setTrade({ ...trade, pips: e.target.value })}
                    readOnly={isAutoCalc}
                    className={isAutoCalc ? "bg-muted/50 border-border cursor-not-allowed" : "bg-muted border-border"}
                    tabIndex={isAutoCalc ? -1 : undefined}
                  />
                </div>

                {/* Entry Date/Time */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="openTime">Entry Date/Time</Label>
                  <Input
                    id="openTime"
                    type="datetime-local"
                    value={trade.openTime}
                    onChange={(e) => setTrade({ ...trade, openTime: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>

                {/* Exit Date/Time */}
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="closeTime">Exit Date/Time</Label>
                  <Input
                    id="closeTime"
                    type="datetime-local"
                    value={trade.closeTime}
                    onChange={(e) => setTrade({ ...trade, closeTime: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>
              </div>

              {/* Commission & Swap */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="commission">Commission <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="commission"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={trade.commission}
                    onChange={(e) => setTrade({ ...trade, commission: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>
                <div className="space-y-1.5 md:space-y-2">
                  <Label htmlFor="swap">Swap <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="swap"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={trade.swap}
                    onChange={(e) => setTrade({ ...trade, swap: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1.5 md:space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  value={trade.notes}
                  onChange={(e) => setTrade({ ...trade, notes: e.target.value })}
                  className="w-full min-h-[80px] md:min-h-[100px] p-3 rounded-md bg-muted border border-border text-foreground"
                  placeholder="Add any additional notes about the trade..."
                />
              </div>

              {/* ── Screenshot section ───────────────────────────────────── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label>Screenshots</Label>
                    {canUpload && (
                      <span className="text-xs text-muted-foreground">
                        ({pendingFiles.length}/{screenshotLimit})
                      </span>
                    )}
                    {canAnalyze && (
                      <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 rounded px-1.5 py-0.5">
                        <Sparkles className="w-3 h-3" />
                        AI Analysis
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">Max 5MB · JPEG, PNG, WebP, GIF</span>
                </div>

                {!canUpload ? (
                  /* Free tier locked state */
                  <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-3 text-muted-foreground">
                    <Lock className="w-4 h-4 flex-shrink-0 text-yellow-500" />
                    <p className="text-xs">
                      Upgrade to <span className="text-blue-400 font-medium">Basic</span> to attach chart screenshots.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Staged file previews */}
                    {pendingFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {pendingFiles.map((item, i) => (
                          <div key={i} className="relative group w-16 h-12">
                            <button
                              type="button"
                              onClick={() => setLightboxIndex(i)}
                              className="w-full h-full focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
                              title="Preview screenshot"
                            >
                              <img
                                src={item.previewUrl}
                                alt={item.file.name}
                                className="w-full h-full object-cover rounded border border-border hover:border-blue-500 transition-colors"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFile(i)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow z-10"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload zone (hidden when at limit) */}
                    {pendingFiles.length < screenshotLimit && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground hover:border-muted-foreground hover:bg-accent/30 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        {pendingFiles.length === 0
                          ? `Add chart screenshot${screenshotLimit > 1 ? 's' : ''}`
                          : `Add more (${screenshotLimit - pendingFiles.length} remaining)`}
                        {canAnalyze && <span className="text-xs text-blue-400">· will be analyzed with AI</span>}
                      </button>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_TYPES.join(',')}
                      multiple={screenshotLimit > 1}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          addFiles(e.target.files);
                          e.target.value = '';
                        }
                      }}
                    />
                  </>
                )}

                {fileError && (
                  <div className="flex items-start gap-2 text-red-400 text-xs bg-red-500/10 rounded p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{fileError}</span>
                    <button type="button" onClick={() => setFileError(null)} className="ml-auto flex-shrink-0">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {/* ── End screenshot section ───────────────────────────────── */}
            </div>

            {/* Sticky Footer */}
            <div className="flex justify-end gap-4 p-4 border-t border-border flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-border hover:bg-accent"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? submitLabel : pendingFiles.length > 0 ? `Save Trade + ${pendingFiles.length} Screenshot${pendingFiles.length > 1 ? 's' : ''}` : 'Save Trade'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {/* Staged screenshot lightbox */}
      {lightboxIndex !== null && pendingFiles[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 z-10">
            <span className="text-white/80 text-sm bg-black/60 rounded px-2 py-1">
              {lightboxIndex + 1} / {pendingFiles.length}
            </span>
          </div>

          {/* Image */}
          <div
            className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={pendingFiles[lightboxIndex].previewUrl}
              alt={pendingFiles[lightboxIndex].file.name}
              className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
            />
          </div>

          {/* Prev / Next */}
          {pendingFiles.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AddTradeModal;
